/**
 * Activity tracking & heatmaps (website roadmap S22 — FF Scouter's Activity Tracker, Torn Intel's activity and heatmaps).
 * One copy for the server (apps/api/src/activity.ts keeps the store and answers), the website (apps/web pages draw it) and
 * any other reader (S20's faction roster uses `ActivityMembersAnswer` / `readActivityMembers` for its 30-day column).
 * Pure, browser-safe.
 *
 * The store: per player and UTC day (Torn's time), two bitmaps of 288 five-minute slots — `active` (they did something in
 * that slot) and `seen` (Omni-Torn knows about that slot: active or not). Everything else (online buckets of 5/15/60 min,
 * hour × day heatmaps, best attack hours, a faction's active ratio, members' 30-day activity) is worked out from those.
 *
 * Fed (terms ACTIVITY_TERMS) by:
 * - the faction watch: each faction's members read every ACTIVITY_WATCH_MINUTES with a key a member gave the Discord bot —
 *   only a key whose owner agreed to ACTIVITY_TERMS and who is still in that faction;
 * - the player database's `activity` sightings, from senders who agreed to ACTIVITY_TERMS.
 * A sighting says "their last action was at L, seen at T": L's slot was active, and every slot after L's up to T's was
 * not (Torn's last action is the latest one) — so one look covers the whole gap since their last action.
 */

/** From this terms version on, other players' activity is kept and shown to Omni-Torn users (key-terms.ts). */
export const ACTIVITY_TERMS = 10;
/** One slot. */
export const ACTIVITY_SLOT_MS = 5 * 60_000;
export const ACTIVITY_SLOTS_PER_DAY = 288;
/** Bytes in one day's bitmap (288 bits). */
export const ACTIVITY_DAY_BYTES = ACTIVITY_SLOTS_PER_DAY / 8;
const DAY_MS = 86_400_000;
/** Days kept (then gone). */
export const ACTIVITY_KEEP_DAYS = 90;
/** "Your faction's members": this many days. */
export const ACTIVITY_MEMBER_DAYS = 30;
/** The faction watch reads each faction's members this often. */
export const ACTIVITY_WATCH_MINUTES = 5;
/** How far back one sighting may cover "no action since" (a player last seen weeks ago doesn't fill weeks). */
export const ACTIVITY_SPAN_MS = 7 * DAY_MS;
/** A page's "2 hours ago" is only to the hour: such a sighting's last action marks no active slot past this age. */
export const ACTIVITY_PAGE_EXACT_MS = 60 * 60_000;
/** Bucket sizes the pages offer (minutes). */
export const ACTIVITY_BUCKETS = [5, 15, 60] as const;
export type ActivityBucket = (typeof ACTIVITY_BUCKETS)[number];
export const isActivityBucket = (x: unknown): x is ActivityBucket => (ACTIVITY_BUCKETS as readonly unknown[]).includes(x);
/** A heatmap cell (or hour) needs this many looks before it says anything. */
export const ACTIVITY_MIN_SEEN = 3;
/** GET /api/activity/faction/:id `days`: 1 to this. */
export const ACTIVITY_FACTION_MAX_DAYS = 30;
/** GET /api/activity/player/:id answers up to this many days. */
export const ACTIVITY_PLAYER_MAX_DAYS = ACTIVITY_KEEP_DAYS;

/** Your faction's members' activity (S22; the Faction → Activity tab). Member-only, own faction. */
export const ACTIVITY_MEMBERS_PATH = '/api/activity/members';
/**
 * The same for S20's roster column (the shape worker s20 reads): `GET /api/faction/activity/members?days=1–30` →
 * `FactionActivityMembersAnswer` — each member `{ id, ratio (0–1, null when under an hour seen), activeHours }`.
 */
export const FACTION_ACTIVITY_MEMBERS_PATH = '/api/faction/activity/members';
export interface FactionActivityMembersAnswer {
  factionId: number;
  at: number;
  days: number;
  watched: boolean;
  members: Array<{ id: number; ratio: number | null; activeHours: number }>;
}

// ---------------------------------------------------------------- slots and bits

export const slotOf = (ms: number): number => Math.floor(ms / ACTIVITY_SLOT_MS);
export const dayOf = (ms: number): number => Math.floor(ms / DAY_MS);
export const dayOfSlot = (slot: number): number => Math.floor(slot / ACTIVITY_SLOTS_PER_DAY);
export const slotStart = (slot: number): number => slot * ACTIVITY_SLOT_MS;

export const emptyBits = (): Uint8Array => new Uint8Array(ACTIVITY_DAY_BYTES);
export const bitGet = (bits: Uint8Array, i: number): boolean => ((bits[i >> 3] ?? 0) & (1 << (i & 7))) !== 0;
export function bitSet(bits: Uint8Array, i: number): void {
  if (i < 0 || i >= ACTIVITY_SLOTS_PER_DAY) return;
  bits[i >> 3] = (bits[i >> 3] ?? 0) | (1 << (i & 7));
}
export function bitCount(bits: Uint8Array): number {
  let n = 0;
  for (const b of bits) {
    let x = b;
    while (x) {
      n += x & 1;
      x >>= 1;
    }
  }
  return n;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
/** Bytes → base64 (no Buffer/btoa: the same in Node and the browser). */
export function bitsToText(bits: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bits.length; i += 3) {
    const a = bits[i]!;
    const b = bits[i + 1];
    const c = bits[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]! + (b === undefined ? '=' : B64[(n >> 6) & 63]!) + (c === undefined ? '=' : B64[n & 63]!);
  }
  return out;
}
/** base64 → one day's bitmap (wrong or short text: the bits it has, the rest empty). */
export function bitsFromText(text: unknown): Uint8Array {
  const out = emptyBits();
  if (typeof text !== 'string') return out;
  const clean = text.replace(/[^A-Za-z0-9+/]/g, '');
  let o = 0;
  for (let i = 0; i + 1 < clean.length && o < out.length; i += 4) {
    const n = (B64.indexOf(clean[i]!) << 18) | (B64.indexOf(clean[i + 1]!) << 12) | (Math.max(0, B64.indexOf(clean[i + 2] ?? 'A')) << 6) | Math.max(0, B64.indexOf(clean[i + 3] ?? 'A'));
    for (const shift of [16, 8, 0]) if (o < out.length) out[o++] = (n >> shift) & 255;
  }
  return out;
}

// ---------------------------------------------------------------- one sighting → slots

export type ActivityState = 'Online' | 'Idle' | 'Offline';
export const activityStateOf = (x: unknown): ActivityState | null => {
  const s = typeof x === 'string' ? x.trim().toLowerCase() : '';
  return s === 'online' ? 'Online' : s === 'idle' ? 'Idle' : s === 'offline' ? 'Offline' : null;
};

export interface ActivitySighting {
  id: number;
  /** When it was seen (ms). */
  at: number;
  /** Their last action (ms), when known. */
  lastAction: number | null;
  state: ActivityState | null;
  /** The last action is exact (Torn's API) — a page's "2 hours ago" is not. */
  exact: boolean;
}

/** What one sighting tells: the active slot (if any) and the slots it covers (seenFrom..seenTo, inclusive). */
export interface ActivityMarks {
  active: number | null;
  seenFrom: number;
  seenTo: number;
}

/**
 * The slots one sighting marks, or null when it says nothing usable (no last action and no state, a time in the future…):
 * - last action L at or before T: L's slot active (unless L is a page's rough "hours ago"), every slot after it up to T's
 *   seen (no action then), at most ACTIVITY_SPAN_MS back;
 * - no last action: Online → T's slot active; Idle/Offline → T's slot seen, not active.
 */
export function sightingMarks(s: ActivitySighting, now: number): ActivityMarks | null {
  if (!Number.isFinite(s.at) || s.at <= 0) return null;
  const at = Math.min(s.at, now);
  if (now - at > ACTIVITY_KEEP_DAYS * DAY_MS) return null;
  const to = slotOf(at);
  const la = s.lastAction !== null && Number.isFinite(s.lastAction) && s.lastAction > 0 ? Math.min(s.lastAction, at) : null;
  if (la === null) {
    if (!s.state) return null;
    return { active: s.state === 'Online' ? to : null, seenFrom: to, seenTo: to };
  }
  const floor = slotOf(at - ACTIVITY_SPAN_MS);
  const laSlot = slotOf(la);
  const exact = s.exact || at - la <= ACTIVITY_PAGE_EXACT_MS;
  if (laSlot < floor) return { active: null, seenFrom: floor, seenTo: to };
  return exact ? { active: laSlot, seenFrom: laSlot, seenTo: to } : laSlot + 1 <= to ? { active: null, seenFrom: laSlot + 1, seenTo: to } : null;
}

export interface DayBitsPair {
  active: Uint8Array;
  seen: Uint8Array;
}

/** Puts one sighting's marks into a player's days (`day` → bits, made when missing). Returns the days touched. */
export function applyMarks(days: Map<number, DayBitsPair>, m: ActivityMarks): number[] {
  const touched = new Set<number>();
  const get = (d: number): DayBitsPair => {
    let x = days.get(d);
    if (!x) days.set(d, (x = { active: emptyBits(), seen: emptyBits() }));
    touched.add(d);
    return x;
  };
  for (let s = m.seenFrom; s <= m.seenTo; s++) bitSet(get(dayOfSlot(s)).seen, s % ACTIVITY_SLOTS_PER_DAY);
  if (m.active !== null) {
    const d = get(dayOfSlot(m.active));
    bitSet(d.active, m.active % ACTIVITY_SLOTS_PER_DAY);
    bitSet(d.seen, m.active % ACTIVITY_SLOTS_PER_DAY);
  }
  return [...touched];
}

// ---------------------------------------------------------------- series, heatmaps, summaries

/** A run of buckets from `from` (ms): per bucket, how many were active and how many were seen (a player: 0 or 1). */
export interface ActivitySeries {
  from: number;
  bucketMin: ActivityBucket;
  active: number[];
  seen: number[];
}

/**
 * One player's buckets from `fromMs` to `toMs` (bucket-aligned; Torn's time): a bucket is active when any slot in it was,
 * seen when any slot in it was.
 */
export function playerSeries(days: ReadonlyMap<number, DayBitsPair>, fromMs: number, toMs: number, bucketMin: ActivityBucket): ActivitySeries {
  const per = bucketMin / 5;
  const first = Math.floor(slotOf(fromMs) / per) * per;
  const last = slotOf(toMs);
  const n = Math.max(0, Math.floor((last - first) / per) + 1);
  const active = new Array<number>(n).fill(0);
  const seen = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i++) {
    for (let k = 0; k < per; k++) {
      const s = first + i * per + k;
      const d = days.get(dayOfSlot(s));
      if (!d) continue;
      const b = s % ACTIVITY_SLOTS_PER_DAY;
      if (bitGet(d.seen, b)) seen[i] = 1;
      if (bitGet(d.active, b)) active[i] = 1;
    }
  }
  return { from: slotStart(first), bucketMin, active, seen };
}

/** Adds `b` into `a` (same from and bucket size) — a faction's series is its members' summed. */
export function addSeries(a: ActivitySeries, b: ActivitySeries): ActivitySeries {
  for (let i = 0; i < a.active.length; i++) {
    a.active[i]! += b.active[i] ?? 0;
    a.seen[i]! += b.seen[i] ?? 0;
  }
  return a;
}

export const emptySeries = (from: number, bucketMin: ActivityBucket, n: number): ActivitySeries => ({ from, bucketMin, active: new Array<number>(n).fill(0), seen: new Array<number>(n).fill(0) });

/** Active share of everything seen (a faction's "active ratio": member-buckets active / member-buckets seen); null when nothing seen. */
export function activeRatio(s: Pick<ActivitySeries, 'active' | 'seen'>): number | null {
  const seen = s.seen.reduce((a, b) => a + b, 0);
  return seen ? s.active.reduce((a, b) => a + b, 0) / seen : null;
}

/** Share of buckets seen at all (a player: how much of the time Omni-Torn knows about). */
export const seenShare = (s: Pick<ActivitySeries, 'seen'>): number => (s.seen.length ? s.seen.filter((x) => x > 0).length / s.seen.length : 0);

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** One cell of an hour × weekday heatmap (Torn's time). `share` = active / seen (null under ACTIVITY_MIN_SEEN looks). */
export interface HeatCell {
  active: number;
  seen: number;
  /** Buckets (hours) with anything seen in them — a faction's average active members = active / looks. */
  looks: number;
  share: number | null;
}

/** heat[weekday 0 = Monday][hour 0–23] from an hourly series (Torn's time = UTC). */
export function heatmap(s: ActivitySeries): HeatCell[][] {
  const heat = WEEKDAYS.map(() => Array.from({ length: 24 }, (): HeatCell => ({ active: 0, seen: 0, looks: 0, share: null })));
  const step = s.bucketMin * 60_000;
  for (let i = 0; i < s.active.length; i++) {
    if (!s.seen[i]) continue;
    const t = new Date(s.from + i * step);
    const cell = heat[(t.getUTCDay() + 6) % 7]![t.getUTCHours()]!;
    cell.active += s.active[i]!;
    cell.seen += s.seen[i]!;
    cell.looks++;
  }
  for (const row of heat) for (const c of row) c.share = c.looks >= ACTIVITY_MIN_SEEN && c.seen ? c.active / c.seen : null;
  return heat;
}

/** The same, folded over the week: per hour of day (0–23). */
export function hourProfile(heat: readonly HeatCell[][]): HeatCell[] {
  return Array.from({ length: 24 }, (_, h) => {
    const c: HeatCell = { active: 0, seen: 0, looks: 0, share: null };
    for (const row of heat) {
      c.active += row[h]!.active;
      c.seen += row[h]!.seen;
      c.looks += row[h]!.looks;
    }
    c.share = c.looks >= ACTIVITY_MIN_SEEN && c.seen ? c.active / c.seen : null;
    return c;
  });
}

/**
 * Best hours to attack (Torn's time): the hours of day with the lowest active share, least active first (ties: the hour
 * seen more). Only hours with ACTIVITY_MIN_SEEN looks; at most `n`.
 */
export function bestHours(heat: readonly HeatCell[][], n = 3): Array<{ hour: number; share: number }> {
  return hourProfile(heat)
    .map((c, hour) => ({ hour, share: c.share, looks: c.looks }))
    .filter((x): x is { hour: number; share: number; looks: number } => x.share !== null)
    .sort((a, b) => a.share - b.share || b.looks - a.looks || a.hour - b.hour)
    .slice(0, n)
    .map(({ hour, share }) => ({ hour, share }));
}

/** "14:00–15:00 TCT". */
export const hourText = (h: number): string => `${String(h).padStart(2, '0')}:00–${String((h + 1) % 24).padStart(2, '0')}:00 TCT`;

/** One member's activity over the last `days` days (S20's roster column; the Faction page's members list). */
export interface MemberActivity {
  id: number;
  /** Slots seen / slots in the window (0–1): how much Omni-Torn knows. */
  coverage: number;
  /** Active time a day (hours): the share of the slots seen that were active × 24; null under an hour seen. */
  hoursPerDay: number | null;
  /** Days with at least one active slot. */
  activeDays: number;
  /** Days with anything seen. */
  seenDays: number;
  /** The newest active slot's start (ms), null when none. */
  lastActive: number | null;
  /** Per hour of day (Torn's time): active share, null under ACTIVITY_MIN_SEEN looks. */
  hours: Array<number | null>;
  /** Share of the slots seen that were active (0–1; null under an hour seen) — S20's roster column. */
  ratio: number | null;
  /** Hours with an action seen in the window (active five-minute slots × 5 minutes) — S20's roster column. */
  activeHours: number;
}

/** One player's summary from their days, over the `days` days up to `now`. */
export function memberActivity(id: number, days: ReadonlyMap<number, DayBitsPair>, now: number, windowDays: number = ACTIVITY_MEMBER_DAYS): MemberActivity {
  const today = dayOf(now);
  let seenSlots = 0;
  let activeSlots = 0;
  let activeDays = 0;
  let seenDays = 0;
  let lastActive: number | null = null;
  for (let d = today - windowDays + 1; d <= today; d++) {
    const x = days.get(d);
    if (!x) continue;
    const s = bitCount(x.seen);
    const a = bitCount(x.active);
    seenSlots += s;
    activeSlots += a;
    if (s) seenDays++;
    if (a) {
      activeDays++;
      for (let b = ACTIVITY_SLOTS_PER_DAY - 1; b >= 0; b--) {
        if (bitGet(x.active, b)) {
          const at = (d * ACTIVITY_SLOTS_PER_DAY + b) * ACTIVITY_SLOT_MS;
          if (lastActive === null || at > lastActive) lastActive = at;
          break;
        }
      }
    }
  }
  const series = playerSeries(days, (today - windowDays + 1) * DAY_MS, now, 60);
  const hours = hourProfile(heatmap(series)).map((c) => (c.share === null ? null : Math.round(c.share * 100) / 100));
  // Active time a day: the share of the slots seen that were active, over a whole day (a day partly seen counts as much
  // as it was seen) — null under an hour seen in all.
  const hoursPerDay = seenSlots >= 12 ? Math.round((activeSlots / seenSlots) * 24 * 10) / 10 : null;
  const ratio = seenSlots >= 12 ? Math.round((activeSlots / seenSlots) * 1000) / 1000 : null;
  const activeHours = Math.round(((activeSlots * 5) / 60) * 10) / 10;
  return { id, coverage: Math.round((seenSlots / (windowDays * ACTIVITY_SLOTS_PER_DAY)) * 1000) / 1000, hoursPerDay, activeDays, seenDays, lastActive, hours, ratio, activeHours };
}

/** "3.4 h a day · active 27 of 30 days" — or why there's nothing. */
export function memberActivityText(m: MemberActivity | null | undefined, windowDays: number = ACTIVITY_MEMBER_DAYS): string {
  if (!m || m.hoursPerDay === null) return 'not seen yet';
  const h = m.hoursPerDay < 0.1 && m.activeDays ? '<0.1' : m.hoursPerDay.toFixed(1);
  return `${h} h a day · active ${m.activeDays} of ${windowDays} days`;
}

// ---------------------------------------------------------------- answers

/** One day of one player, as the server sends it (bits as base64). */
export interface ActivityDayWire {
  day: number;
  active: string;
  seen: string;
}

/** GET /api/activity/player/:id — that player's kept days (newest last), invite-only. */
export interface ActivityPlayerAnswer {
  id: number;
  at: number;
  /** Days kept; 0 = no end date (terms KEEP_ALL_TERMS, "No age limits"). */
  keepDays: number;
  days: ActivityDayWire[];
}

/** GET /api/activity/faction/:id?days=&bucket= — a faction's members (its roster now) added up, invite-only. */
export interface ActivityFactionAnswer {
  factionId: number;
  at: number;
  days: number;
  /** Members counted (the roster Omni-Torn knows) and how many of them have anything kept. */
  members: number;
  covered: number;
  /** Omni-Torn's faction watch reads this faction (a member gave the Discord bot a key and agreed to these terms). */
  watched: boolean;
  series: ActivitySeries;
}

/** GET /api/activity/members — your faction's members, ACTIVITY_MEMBER_DAYS days (member-only). */
export interface ActivityMembersAnswer {
  factionId: number;
  at: number;
  days: number;
  watched: boolean;
  members: MemberActivity[];
}

/** Days off the wire → the map the functions above take. */
export function daysFromWire(list: readonly ActivityDayWire[] | null | undefined): Map<number, DayBitsPair> {
  const out = new Map<number, DayBitsPair>();
  for (const d of list ?? []) if (Number.isInteger(d?.day)) out.set(d.day, { active: bitsFromText(d.active), seen: bitsFromText(d.seen) });
  return out;
}

const num = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);

/** A GET /api/activity/members answer, checked (null when it isn't one). */
export function readActivityMembers(body: unknown): ActivityMembersAnswer | null {
  const b = body as Partial<ActivityMembersAnswer> | null;
  if (!b || typeof b !== 'object' || !Number.isInteger(b.factionId) || !Array.isArray(b.members)) return null;
  const members: MemberActivity[] = [];
  for (const m of b.members as unknown[]) {
    const x = m as Partial<MemberActivity> | null;
    if (!x || !Number.isInteger(x.id)) continue;
    members.push({
      id: x.id!,
      coverage: num(x.coverage) ?? 0,
      hoursPerDay: num(x.hoursPerDay),
      activeDays: num(x.activeDays) ?? 0,
      seenDays: num(x.seenDays) ?? 0,
      lastActive: num(x.lastActive),
      hours: Array.isArray(x.hours) ? Array.from({ length: 24 }, (_, i) => num(x.hours![i])) : new Array<number | null>(24).fill(null),
      ratio: num(x.ratio),
      activeHours: num(x.activeHours) ?? 0,
    });
  }
  return { factionId: b.factionId!, at: num(b.at) ?? 0, days: num(b.days) ?? ACTIVITY_MEMBER_DAYS, watched: b.watched === true, members };
}
